import { lazy, useEffect, useMemo, useRef, useState } from 'react'
import { Eyebrow, PrismLight, Segments } from '@/components/ui'
import { storefront } from '@/content/site'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { useReducedMotion } from '@/hooks/useReducedMotion'
import { useScrollProgress } from '@/hooks/useScrollProgress'
import { cx } from '@/lib/cx'
import { DURATION, gsap, SplitText, useGSAP } from '@/lib/gsap'
import { useQuality } from '@/lib/quality'
import { useAppStore } from '@/lib/store'
import { hasWebGL2 } from '@/webgl/capabilities'
import { SceneCanvas } from '@/webgl/SceneCanvas'
import { usePrefetchAfterIntro } from '@/webgl/prefetch'
import { withRetry } from '@/webgl/retry'
import styles from './Storefront.module.css'
import type { SceneLayout, StorefrontOverlay } from './StorefrontScene'

const loadStorefrontScene = () => import('./StorefrontScene')
const StorefrontScene = lazy(() =>
  withRetry(loadStorefrontScene).then((module) => ({
    default: module.StorefrontScene,
  })),
)

const MODEL_URL = `${import.meta.env.BASE_URL}models/storefront.glb`
/** Same scene with a half-size AO atlas, for the low tier. */
const MODEL_URL_LOW = `${import.meta.env.BASE_URL}models/storefront-1k.glb`
/** Phones and portrait tablets get the docked, one-card-at-a-time layout. */
const COMPACT_QUERY = '(max-width: 1023px)'

/**
 * "What it does" (#storefront). A sunlit pastel Blender diorama of Sharma Sweets, pinned
 * while the scroll brings it online: the shutter rolls up slat by slat, the bulbs warm on,
 * the sign and screens glow. Then the eight capabilities fan out of the shop as glass cards
 * that orbit it, each tied by a hairline to the part of the shop it's about.
 *
 * Wide screens: cards in two arcs around the shop, tilting toward the pointer. Compact
 * screens: a docked carousel, one capability at a time, the camera leaning toward its part
 * of the shop. Reduced motion (or no WebGL): the shop online in a single still, with the
 * cards laid out as a grid. The cards are real DOM text throughout.
 */
export function Storefront() {
  usePrefetchAfterIntro(loadStorefrontScene)
  const sectionRef = useRef<HTMLElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const stageRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLElement>(null)
  const leadRef = useRef<HTMLParagraphElement>(null)
  const cardRefs = useRef<(HTMLLIElement | null)[]>([])
  const pathRefs = useRef<(SVGPathElement | null)[]>([])
  const dotRefs = useRef<(SVGCircleElement | null)[]>([])

  const reducedMotion = useReducedMotion()
  const compact = useMediaQuery(COMPACT_QUERY)
  const quality = useQuality()
  const introStarted = useAppStore((state) => state.phase !== 'boot')
  const pageReady = useAppStore((state) => state.phase === 'ready')
  // Pick the asset once: a later quality drop must not reload the model mid-scroll.
  const [modelUrl] = useState(() => (quality.tier === 'low' ? MODEL_URL_LOW : MODEL_URL))
  useEffect(() => {
    const connection = (
      navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }
    ).connection
    if (
      !pageReady ||
      !hasWebGL2() ||
      connection?.saveData ||
      connection?.effectiveType?.includes('2g')
    )
      return
    let cancelled = false
    const preload = () => {
      void import('./preload').then(({ preloadStorefront }) => {
        if (!cancelled) preloadStorefront(modelUrl)
      })
    }
    const idle = 'requestIdleCallback' in window
    const task = idle ? window.requestIdleCallback(preload) : window.setTimeout(preload, 1200)
    return () => {
      cancelled = true
      if (idle) window.cancelIdleCallback(task)
      else window.clearTimeout(task)
    }
  }, [pageReady, modelUrl])
  const live = !reducedMotion && hasWebGL2()
  const layout: SceneLayout = !live ? 'still' : compact ? 'compact' : 'wide'
  const progress = useScrollProgress(trackRef, { start: 'top top', end: 'bottom bottom' })

  const overlay = useMemo<StorefrontOverlay>(
    () => ({
      stage: stageRef,
      header: headerRef,
      lead: leadRef,
      cards: cardRefs,
      paths: pathRefs,
      dots: dotRefs,
    }),
    [],
  )

  useGSAP(
    () => {
      if (!introStarted) return
      if (reducedMotion) {
        gsap.from('[data-reveal]', { autoAlpha: 0, duration: 0.3 })
        return
      }
      const title = sectionRef.current?.querySelector<HTMLElement>('[data-title]')
      if (!title) return
      const trigger = { trigger: sectionRef.current, start: 'top 80%', once: true }
      gsap.from('[data-reveal]', {
        autoAlpha: 0,
        y: 18,
        duration: DURATION.cinematic,
        stagger: 0.12,
        scrollTrigger: trigger,
      })
      SplitText.create(title, {
        type: 'lines',
        mask: 'lines',
        autoSplit: true,
        onSplit: (self) =>
          gsap.from(self.lines, {
            yPercent: 105,
            stagger: 0.08,
            duration: DURATION.cinematic,
            scrollTrigger: trigger,
          }),
      })
    },
    { scope: sectionRef, dependencies: [introStarted, reducedMotion] },
  )

  return (
    <section
      id="storefront"
      ref={sectionRef}
      aria-labelledby="storefront-title"
      className={cx(
        styles.root,
        layout === 'still' ? styles.still : styles.live,
        layout === 'compact' && styles.compact,
      )}
      data-theme="light"
    >
      <div ref={trackRef} className={styles.track}>
        <div ref={stageRef} className={styles.stage}>
          <PrismLight
            className={styles.prism}
            placement={layout === 'compact' ? 'bottom' : 'center'}
            intensity={0.75}
          />
          <div className={styles.canvas}>
            <SceneCanvas
              id="storefront"
              label={storefront.sceneLabel}
              onRetry={() =>
                import('./preload').then(({ clearStorefront }) => clearStorefront(modelUrl))
              }
              toneMapping="neutral"
              camera={{ fov: 30, position: [10, 6, 14], near: 0.5, far: 200 }}
              shadows={quality.shadows}
              onCreated={({ gl }) => {
                gl.toneMappingExposure = 1.1
              }}
            >
              <StorefrontScene
                url={modelUrl}
                progress={progress}
                layout={layout}
                shadows={quality.shadows}
                overlay={overlay}
              />
            </SceneCanvas>
          </div>

          <svg className={styles.connectors} aria-hidden="true" focusable="false">
            {storefront.capabilities.map((capability, index) => (
              <g key={capability.id}>
                <path
                  ref={(node) => {
                    pathRefs.current[index] = node
                  }}
                  className={styles.connector}
                  pathLength={1}
                />
                <circle
                  ref={(node) => {
                    dotRefs.current[index] = node
                  }}
                  className={styles.pin}
                  r={3.5}
                />
              </g>
            ))}
          </svg>

          <header ref={headerRef} className={styles.header}>
            <Eyebrow data-reveal>{storefront.eyebrow}</Eyebrow>
            <h2 id="storefront-title" className={styles.title} data-title>
              <Segments segments={storefront.title} />
            </h2>
            <p ref={leadRef} className={styles.lead} data-reveal>
              {storefront.lead}
            </p>
          </header>

          <ul className={styles.cards} role="list">
            {storefront.capabilities.map((capability, index) => (
              <li
                key={capability.id}
                ref={(node) => {
                  cardRefs.current[index] = node
                }}
                className={cx('glass-prism', styles.card)}
                data-capability={capability.id}
                onPointerEnter={() => pathRefs.current[index]?.setAttribute('data-active', '')}
                onPointerLeave={() => pathRefs.current[index]?.removeAttribute('data-active')}
              >
                <span className={styles.index} aria-hidden="true">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <h3 className={styles.cardTitle}>{capability.title}</h3>
                <p className={styles.cardBody}>{capability.body}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  )
}
